'use client';

import { useCallback, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { HeroLegibleAnimation } from "@/components/marketing/HeroLegibleAnimation";

export default function HomeHeroSection() {
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState("");

  const handleAnalyze = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      let val = url.trim();
      if (!val) {
        setUrlError("Enter your website address.");
        return;
      }
      if (!val.startsWith("http://") && !val.startsWith("https://")) val = `https://${val}`;
      try {
        new URL(val);
      } catch {
        setUrlError("Enter a valid domain (e.g. www.example.com).");
        return;
      }
      setUrlError("");
      router.push(`/preview?url=${encodeURIComponent(val)}`);
    },
    [url, router],
  );

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  return (
    <section id="hero" className="hash-bg-dark relative pt-20 pb-28 overflow-hidden">
      <HeroLegibleAnimation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10 text-center">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-8">
          <motion.div variants={fadeIn}>
            <span className="inline-block px-3 py-1 text-xs font-bold tracking-widest uppercase rounded-full bg-blue-600 text-white">
              AI visibility for growing brands
            </span>
          </motion.div>

          <motion.h1 variants={fadeIn} className="text-5xl sm:text-6xl md:text-7xl font-display font-extrabold tracking-tight text-white leading-[1.06]">
            Be the answer
            <br />
            <span className="text-blue-400">AI gives.</span>
          </motion.h1>

          <motion.p variants={fadeIn} className="text-xl text-blue-100/80 max-w-2xl mx-auto leading-snug">
            Two companies publish the same quality content.<br className="hidden sm:inline" />
            Only one gets cited by ChatGPT. Here&apos;s what separates them.
          </motion.p>

          <motion.p variants={fadeIn} className="text-sm text-blue-300/70 max-w-2xl mx-auto leading-relaxed">
            The difference isn&apos;t what you write — it&apos;s how AI reads it. Enter your URL to see which side you&apos;re on.
          </motion.p>

          <motion.div variants={fadeIn} className="max-w-2xl mx-auto">
            <form onSubmit={handleAnalyze}>
              <div className="flex flex-col sm:flex-row gap-2 shadow-2xl shadow-black/40">
                <input
                  ref={inputRef}
                  type="text"
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setUrlError("");
                  }}
                  placeholder="www.yoursite.com"
                  className="flex-1 px-5 py-4 text-base rounded-xl sm:rounded-r-none bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-inset"
                  autoComplete="url"
                  inputMode="url"
                />
                <button
                  type="submit"
                  className="px-7 py-4 bg-blue-600 text-white font-bold text-base rounded-xl sm:rounded-l-none hover:bg-blue-500 transition-colors whitespace-nowrap"
                >
                  Analyze my AI visibility →
                </button>
              </div>
              {urlError && (
                <p className="mt-2 text-sm text-red-400 text-left">{urlError}</p>
              )}
            </form>
            <p className="mt-3 text-sm text-blue-300/60">Free. Takes ~10 seconds. No account needed.</p>
          </motion.div>

          <motion.div variants={fadeIn} className="flex flex-col items-center gap-3">
            <span className="text-xs text-blue-300/40 uppercase tracking-widest">Works with any website</span>
            <div className="flex items-center gap-4 text-xs text-blue-200/50 font-medium">
              {["Webflow", "WordPress", "Drupal", "Shopify", "+ more"].map((name) => (
                <span key={name} className="px-3 py-1 rounded-full border border-white/10 bg-white/5">
                  {name}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div variants={fadeIn} className="flex flex-col sm:flex-row items-center justify-center gap-8 pt-4 border-t border-white/10">
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-white font-display">16</span>
              <span className="text-sm text-blue-300/70 font-medium">AI signals managed</span>
            </div>
            <div className="hidden sm:block w-px h-10 bg-white/15" />
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-white font-display">20+</span>
              <span className="text-sm text-blue-300/70 font-medium">AI crawlers tracked</span>
            </div>
            <div className="hidden sm:block w-px h-10 bg-white/15" />
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-white font-display">&lt; 5 min</span>
              <span className="text-sm text-blue-300/70 font-medium">From URL to live</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
